import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, ChevronDown, ChevronUp, CheckCircle2, XCircle, MessageSquare, ClipboardList } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { TranscriptEntry } from './GeminiLiveWidget';

// Legacy JSON format (for backward compatibility with old sessions)
interface AnalysisSection {
  score: string;
  strengths: string[];
  weaknesses: string[];
}

interface Correction {
  error: string;
  correction: string;
  category: string;
}

interface LegacyAnalysisResult {
  analysis_by_section: {
    gespraechsfuehrung: AnalysisSection;
    laienverstaendliche_sprache: AnalysisSection;
    vollstaendigkeit: AnalysisSection;
    sprachliche_richtigkeit: AnalysisSection;
    empathie: AnalysisSection;
  };
  corrections: Correction[];
  overall_grade: string;
  reason: string;
  detailed_feedback: string;
}

interface FeedbackDisplayProps {
  analysis: string | LegacyAnalysisResult;
  transcript: TranscriptEntry[];
}

const sectionTitles: Record<string, string> = {
  gespraechsfuehrung: 'Gesprächsführung',
  laienverstaendliche_sprache: 'Laienverständliche Sprache',
  vollstaendigkeit: 'Vollständigkeit',
  sprachliche_richtigkeit: 'Sprachliche Richtigkeit',
  empathie: 'Empathie'
};

const scoreColors: Record<string, string> = {
  gut: '#10b981',
  befriedigend: '#f59e0b',
  mangelhaft: '#ef4444'
};

export function FeedbackDisplay({ analysis, transcript }: FeedbackDisplayProps) {
  const [showTranscript, setShowTranscript] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  // Check if analysis is Markdown (string) or Legacy JSON (object)
  const isMarkdown = typeof analysis === 'string';

  const groupedTranscript = transcript.reduce((acc: { role: 'user' | 'model'; texts: string[]; timestamp: number }[], entry) => {
    const lastGroup = acc[acc.length - 1];
    if (lastGroup && lastGroup.role === entry.role) {
      lastGroup.texts.push(entry.text);
    } else {
      acc.push({
        role: entry.role,
        texts: [entry.text],
        timestamp: entry.timestamp
      });
    }
    return acc;
  }, []);

  // Render Modern Markdown Version
  if (isMarkdown) {
    return (
      <div className="w-full max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Markdown Content */}
          <div className="glass-card p-6 sm:p-8 markdown-report">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {analysis}
            </ReactMarkdown>
          </div>

          {/* Transcript */}
          <div className="glass-card p-4 sm:p-6">
            <button
              type="button"
              onClick={() => setShowTranscript(!showTranscript)}
              className="w-full flex items-center justify-between select-none hover:bg-white/5 p-2 -m-2 rounded-lg transition-colors"
            >
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5" style={{ color: '#6ee7ff' }} />
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text)' }}>
                  Gesprächstranskript ({transcript.length} Einträge)
                </h3>
              </div>
              {showTranscript ? (
                <ChevronUp className="w-6 h-6 sm:w-5 sm:h-5 flex-shrink-0" style={{ color: 'var(--muted)' }} />
              ) : (
                <ChevronDown className="w-6 h-6 sm:w-5 sm:h-5 flex-shrink-0" style={{ color: 'var(--muted)' }} />
              )}
            </button>

            <AnimatePresence initial={false}>
              {showTranscript && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                >
                  <div className="mt-6 space-y-2 max-h-96 overflow-y-auto pr-2 custom-scrollbar">
                    {groupedTranscript.map((group, idx) => (
                      <div
                        key={idx}
                        className="p-3 rounded-lg"
                        style={{
                          background: group.role === 'user' 
                            ? 'rgba(110, 231, 255, 0.08)'
                            : 'rgba(255, 255, 255, 0.04)',
                          borderLeft: `3px solid ${group.role === 'user' ? '#6ee7ff' : '#8c96ff'}`
                        }}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold" style={{
                            color: group.role === 'user' ? '#6ee7ff' : '#8c96ff'
                          }}>
                            {group.role === 'user' ? 'Arzt' : 'Patient'}
                          </span>
                          <span className="text-xs" style={{ color: 'var(--muted)' }}>
                            {new Date(group.timestamp).toLocaleTimeString('de-DE')}
                          </span>
                        </div>
                        <p className="text-sm leading-relaxed" style={{ color: 'var(--text)' }}>
                          {group.texts.join(' ')}
                        </p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    );
  }

  // Legacy JSON Rendering (for backward compatibility with old sessions)
  const legacyAnalysis = analysis as LegacyAnalysisResult;
  const isPassed = legacyAnalysis.overall_grade.toLowerCase().includes('bestanden') && 
                   !legacyAnalysis.overall_grade.toLowerCase().includes('nicht');

  return (
    <div className="w-full max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* Legacy JSON Rendering - Same as before */}
        <div className="glass-card p-6 sm:p-8">
          <div className="flex items-center justify-center gap-4 mb-4">
            {isPassed ? (
              <CheckCircle2 className="w-12 h-12 sm:w-16 h-16" style={{ color: '#10b981' }} />
            ) : (
              <XCircle className="w-12 h-12 sm:w-16 h-16" style={{ color: '#ef4444' }} />
            )}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold" style={{ 
                color: isPassed ? '#10b981' : '#ef4444'
              }}>
                {legacyAnalysis.overall_grade}
              </h2>
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                FSP-Prüfung Ergebnis
              </p>
            </div>
          </div>
          
          <div className="mt-6 p-4 rounded-xl" style={{
            background: 'rgba(110, 231, 255, 0.08)',
            border: '1px solid rgba(110, 231, 255, 0.2)'
          }}>
            <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text)' }}>
              Hauptgrund:
            </h3>
            <p className="text-sm" style={{ color: 'var(--muted)' }}>
              {legacyAnalysis.reason}
            </p>
          </div>

          <div className="mt-4 p-4 rounded-xl" style={{
            background: 'rgba(255, 255, 255, 0.04)'
          }}>
            <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text)' }}>
              Detailliertes Feedback:
            </h3>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--muted)' }}>
              {legacyAnalysis.detailed_feedback}
            </p>
          </div>
        </div>

        {/* Analysis by Section */}
        <div className="glass-card p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-4">
            <ClipboardList className="w-5 h-5" style={{ color: '#6ee7ff' }} />
            <h3 className="text-lg font-semibold" style={{ color: 'var(--text)' }}>
              Bewertung nach Kriterien
            </h3>
          </div>

          <div className="space-y-3">
            {Object.entries(legacyAnalysis.analysis_by_section).map(([key, section]) => {
              const isExpanded = expandedSections.has(key);
              const scoreColor = scoreColors[section.score.toLowerCase()] || '#6ee7ff';
              const hasContent = (section.strengths?.length ?? 0) > 0 || (section.weaknesses?.length ?? 0) > 0;

              return (
                <div key={key} className="rounded-xl overflow-hidden" style={{
                  background: 'rgba(255, 255, 255, 0.04)',
                  border: '1px solid rgba(255, 255, 255, 0.08)'
                }}>
                  <button
                    type="button"
                    onClick={() => hasContent && toggleSection(key)}
                    className={`w-full px-3 py-2.5 sm:px-4 sm:py-3 flex items-center justify-between transition-colors select-none ${
                      hasContent ? 'hover:bg-white/5 cursor-pointer' : 'cursor-default opacity-70'
                    }`}
                  >
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                      <div
                        className="w-3 h-3 sm:w-2 sm:h-2 rounded-full flex-shrink-0 aspect-square"
                        style={{ background: scoreColor }}
                      />
                      <span className="font-medium text-sm md:text-base" style={{ color: 'var(--text)' }}>
                        {sectionTitles[key] || key}
                      </span>
                      <span
                        className="text-[11px] sm:text-[10px] font-bold px-2.5 py-1 sm:px-2 sm:py-0.5 rounded uppercase tracking-wider flex-shrink-0 whitespace-nowrap"
                        style={{
                          background: `${scoreColor}20`,
                          color: scoreColor,
                          border: `1px solid ${scoreColor}30`
                        }}
                      >
                        {section.score}
                      </span>
                    </div>
                    {hasContent && (
                      isExpanded ? (
                        <ChevronUp className="w-5 h-5 sm:w-4 sm:h-4 flex-shrink-0" style={{ color: 'var(--muted)' }} />
                      ) : (
                        <ChevronDown className="w-5 h-5 sm:w-4 sm:h-4 flex-shrink-0" style={{ color: 'var(--muted)' }} />
                      )
                    )}
                  </button>

                  <AnimatePresence initial={false}>
                    {isExpanded && hasContent && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                      >
                        <div className="px-3 pb-3 pt-1 sm:px-4 sm:pb-4 space-y-4">
                          {(section.strengths?.length ?? 0) > 0 && (
                            <div>
                              <h4 className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#10b981' }}>
                                Stärken:
                              </h4>
                              <ul className="space-y-1.5">
                                {section.strengths?.map((strength, idx) => (
                                  <li key={idx} className="text-sm flex items-start gap-2 text-white/70">
                                    <span className="text-[#10b981] mt-0.5">✓</span>
                                    {strength}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {(section.weaknesses?.length ?? 0) > 0 && (
                            <div>
                              <h4 className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#f59e0b' }}>
                                Verbesserungsmöglichkeiten:
                              </h4>
                              <ul className="space-y-1.5">
                                {section.weaknesses?.map((weakness, idx) => (
                                  <li key={idx} className="text-sm flex items-start gap-2 text-white/70">
                                    <span className="text-[#f59e0b] mt-0.5">→</span>
                                    {weakness}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>

        {/* Corrections Table */}
        {legacyAnalysis.corrections.length > 0 && (
          <div className="glass-card p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-5 h-5" style={{ color: '#6ee7ff' }} />
              <h3 className="text-lg font-semibold" style={{ color: 'var(--text)' }}>
                Korrekturen ({legacyAnalysis.corrections.length})
              </h3>
            </div>

            <div className="space-y-2 sm:space-y-3">
              {legacyAnalysis.corrections.map((correction, idx) => (
                <div
                  key={idx}
                  className="rounded-xl overflow-hidden"
                  style={{
                    background: 'rgba(239, 68, 68, 0.04)',
                    border: '1px solid rgba(239, 68, 68, 0.15)'
                  }}
                >
                  <div className="px-4 py-2 bg-red-500/10 border-b border-red-500/10 flex items-center justify-between">
                    <span className="text-[11px] sm:text-[10px] font-bold uppercase tracking-wider text-red-400">
                      {correction.category}
                    </span>
                  </div>

                  <div className="p-3 sm:p-4 space-y-2 sm:space-y-3">
                    <div>
                      <span className="text-[11px] sm:text-[10px] font-bold uppercase tracking-wider text-red-500/60 block mb-1">
                        Fehler:
                      </span>
                      <p className="text-sm text-white/70 line-through decoration-red-500/40">
                        {correction.error}
                      </p>
                    </div>
                    
                    <div className="pt-2 border-t border-white/5">
                      <span className="text-[11px] sm:text-[10px] font-bold uppercase tracking-wider text-[#10b981] block mb-1">
                        Korrektur:
                      </span>
                      <p className="text-sm font-medium text-white">
                        {correction.correction}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="glass-card p-4 sm:p-6">
          <button
            type="button"
            onClick={() => setShowTranscript(!showTranscript)}
            className="w-full flex items-center justify-between select-none hover:bg-white/5 p-2 -m-2 rounded-lg transition-colors"
          >
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5" style={{ color: '#6ee7ff' }} />
              <h3 className="text-lg font-semibold" style={{ color: 'var(--text)' }}>
                Gesprächstranskript ({transcript.length} Einträge)
              </h3>
            </div>
            {showTranscript ? (
              <ChevronUp className="w-6 h-6 sm:w-5 sm:h-5 flex-shrink-0" style={{ color: 'var(--muted)' }} />
            ) : (
              <ChevronDown className="w-6 h-6 sm:w-5 sm:h-5 flex-shrink-0" style={{ color: 'var(--muted)' }} />
            )}
          </button>

          <AnimatePresence initial={false}>
            {showTranscript && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
              >
                <div className="mt-6 space-y-2 max-h-96 overflow-y-auto pr-2 custom-scrollbar">
                  {groupedTranscript.map((group, idx) => (
                    <div
                      key={idx}
                      className="p-3 rounded-lg"
                      style={{
                        background: group.role === 'user' 
                          ? 'rgba(110, 231, 255, 0.08)'
                          : 'rgba(255, 255, 255, 0.04)',
                        borderLeft: `3px solid ${group.role === 'user' ? '#6ee7ff' : '#8c96ff'}`
                      }}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold" style={{
                          color: group.role === 'user' ? '#6ee7ff' : '#8c96ff'
                        }}>
                          {group.role === 'user' ? 'Arzt' : 'Patient'}
                        </span>
                        <span className="text-xs" style={{ color: 'var(--muted)' }}>
                          {new Date(group.timestamp).toLocaleTimeString('de-DE')}
                        </span>
                      </div>
                      <p className="text-sm leading-relaxed" style={{ color: 'var(--text)' }}>
                        {group.texts.join(' ')}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
